import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import parse from 'html-react-parser';
import isEmpty from 'lodash-es/isEmpty';
import sanitizeHtml from 'sanitize-html';

import Label from '../../../../components/shared/Label/Label';
import Section from '../../../../components/shared/Section/Section';
import { SKILLS_KEYS } from '../../../../constants/commonConstants.js';
import MatchingContactsList from '../MatchingContacts/MatchingContactsList';
import { OpeningReferralCard } from '../OpeningReferralCard/OpeningReferralCard';
import OpeningReferralRewards from '../OpeningReferralRewards/OpeningReferralRewards';
import SmartRewardBadge from '../SmartRewardBadge/SmartRewardBadge';
import UnlockReferralRewards from '../UnlockReferralRewards/UnlockReferralRewards';

import styles from './OpeningDescription.module.scss';

const OpeningDescription = ({
  skills,
  description,
  referralProviderDetails,
  avatarThumb,
  referralRewardDetails,
  isJobPreview,
  showPublicRewards,
  isLoggedInUser,
  externalTcs,
  eligibileForRewards,
  setDescriptionOffset,
  isSticky,
  jobId,
  opening,
  rewardVisibility,
}) => {
  const { t } = useTranslation('common');
  const descriptionRef = useRef(null);
  const getSkillLabel = (name) => (SKILLS_KEYS[name] ? t(`skills.${SKILLS_KEYS[name]}`) : name);

  const SECTIONS = useMemo(() => {
    const sections = [];

    if (!isEmpty(referralProviderDetails) && !isJobPreview) {
      sections.push({ key: 'referralMessage', label: t('browse.sections.referralMessage') });
    }

    if (!isEmpty(description) || skills.length > 0) {
      sections.push({ key: 'aboutJob', label: t('browse.sections.aboutJob') });
    }

    if (
      (referralRewardDetails && referralRewardDetails.length > 0) ||
      (rewardVisibility && rewardVisibility.visibility !== 'none')
    ) {
      sections.push({ key: 'rewards', label: t('browse.sections.rewards') });
    }

    if (isLoggedInUser && jobId) {
      sections.push({ key: 'recommendations', label: t('browse.sections.recommendations') });
    }

    return sections;
  }, [referralProviderDetails, isJobPreview, description, skills, referralRewardDetails, isLoggedInUser, jobId, t]);

  const [selectedHeader, setSelectedHeader] = useState('');

  const sectionRefs = useRef({
    referralMessage: React.createRef(),
    aboutJob: React.createRef(),
    rewards: React.createRef(),
    recommendations: React.createRef(),
  });

  useEffect(() => {
    const paragraphs = document.querySelectorAll('p');
    const strongs = document.querySelectorAll('strong');
    const headingTags = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const allElements = document.querySelectorAll('*');

    paragraphs.forEach((p) => {
      const paragraphHtml = p.innerHTML.trim();
      const onlyBr = paragraphHtml === '<br>' || paragraphHtml === '<br/>' || paragraphHtml === '<br />';
      if (paragraphHtml === '&nbsp;' || onlyBr) {
        p.classList.add(styles.noPadding);
      }
    });

    strongs.forEach((strong) => {
      const strongHtml = strong.innerHTML.trim();
      const onlyBr = strongHtml === '<br>' || strongHtml === '<br/>' || strongHtml === '<br />';
      if (strongHtml === '&nbsp;' || onlyBr) {
        strong.classList.add(styles.displayNone);
      }
    });

    headingTags.forEach((heading) => {
      const prev = heading.previousSibling;
      if (prev && prev.nodeType === 1 && prev.tagName === 'BR') {
        prev.classList.add(styles.displayNone);
      }

      const next = heading.nextSibling;
      if (next && next.nodeType === 1 && next.tagName === 'BR') {
        next.classList.add(styles.displayNone);
      }
    });

    allElements.forEach((el) => {
      const content = el.innerHTML.trim();
      const onlyBr = content === '<br>' || content === '<br/>' || content === '<br />';
      if (content === '&nbsp;' || onlyBr) {
        el.classList.add(styles.displayNone);
      }
    });
  }, []);

  useEffect(() => {
    if (SECTIONS.length > 0 && !selectedHeader) {
      setSelectedHeader(SECTIONS[0].key);
    }
  }, [SECTIONS, selectedHeader]);

  useEffect(() => {
    setDescriptionOffset(descriptionRef.current?.offsetTop);
  }, [descriptionRef.current, setDescriptionOffset]);

  const findScrollableParent = (element) => {
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (overflow.includes('scroll') || overflow.includes('auto')) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  };

  const cleanHtml = sanitizeHtml(description?.replace(/(<br>\s*){2,}/gi, '<br><br>'));
  const cleanRewardHtml = sanitizeHtml(externalTcs);

  const scrollToSection = (sectionKey) => {
    const targetElement =
      sectionKey === SECTIONS[0]?.key ? descriptionRef.current : sectionRefs.current[sectionKey]?.current;

    if (targetElement) {
      const scrollableParent = findScrollableParent(targetElement);

      if (scrollableParent) {
        const parentRect = scrollableParent.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const currentScrollTop = scrollableParent.scrollTop || 0;

        const scrollOffset = currentScrollTop + targetRect.top - parentRect.top - 20;

        scrollableParent.scrollTo({
          top: scrollOffset,
          behavior: 'smooth',
        });
      } else {
        const container = targetElement.closest('[data-scroll-container]') || document.documentElement;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();

        const scrollTop = container.scrollTop + targetRect.top - containerRect.top - 100;

        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleOnClick = (sectionKey) => {
    setSelectedHeader(sectionKey);
    scrollToSection(sectionKey);
  };

  const renderRewardSection = () => {
    if (rewardVisibility && rewardVisibility.visibility !== 'none') {
      if (rewardVisibility.visibility === 'full') {
        return !isEmpty(referralRewardDetails) ? (
          <OpeningReferralRewards
            rewards={referralRewardDetails}
            externalTcs={cleanRewardHtml}
            eligibileForRewards={eligibileForRewards}
            showPublicRewards={showPublicRewards}
          />
        ) : null;
      }
      return <SmartRewardBadge rewardVisibility={rewardVisibility} />;
    }

    if (!rewardVisibility) {
      if (!isEmpty(referralRewardDetails) && (showPublicRewards || isLoggedInUser)) {
        return (
          <OpeningReferralRewards
            rewards={referralRewardDetails}
            externalTcs={cleanRewardHtml}
            eligibileForRewards={eligibileForRewards}
            showPublicRewards={showPublicRewards}
          />
        );
      }
      if (!isEmpty(referralRewardDetails)) {
        return <UnlockReferralRewards />;
      }
    }

    return null;
  };

  return (
    <div className={styles.descriptionContainer} ref={descriptionRef}>
      <div
        className={classNames(
          { [styles.descriptionHeader]: !SECTIONS.length <= 0 },
          { [styles.sticky]: isSticky },
          { [styles.stickyJobPreview]: isJobPreview }
        )}
      >
        {SECTIONS.map((section) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div
            key={section.key}
            className={classNames(styles.headerButton, {
              [styles.headerButtonSelected]: selectedHeader === section.key,
            })}
            role="button"
            onClick={() => handleOnClick(section.key)}
          >
            {section.label}
          </div>
        ))}
      </div>

      {!isEmpty(referralProviderDetails) && !isJobPreview ? (
        <div id="referralMessage" ref={sectionRefs.current.referralMessage}>
          <OpeningReferralCard referralProviderDetails={referralProviderDetails} avatarThumb={avatarThumb} />
        </div>
      ) : null}

      {!isEmpty(description) || skills.length > 0 ? (
        <div className={styles.descriptionSection} id="aboutJob" ref={sectionRefs.current.aboutJob}>
          {!isEmpty(description) ? <div className={styles.aboutSection}>{parse(cleanHtml)}</div> : null}

          {skills.length > 0 ? (
            <Section
              heading={t('filterHeadings.skills')}
              content={
                <Label tags={skills.map((skill) => getSkillLabel(skill?.name ?? skill))} classname={styles.skills} />
              }
            />
          ) : null}
        </div>
      ) : null}

      <div id="rewards" ref={sectionRefs.current.rewards} className={styles.rewardSection}>
        {renderRewardSection()}
      </div>

      {isLoggedInUser && jobId ? (
        <div id="recommendations" ref={sectionRefs.current.recommendations} className={styles.recommendationsSection}>
          <MatchingContactsList jobId={jobId} opening={opening} />
        </div>
      ) : null}
    </div>
  );
};

export default OpeningDescription;
